import { round, sumBy } from 'lodash-es'

import type {
  FullShipment,
  LoadDocument,
  PickupDetail,
  ShipmentLocation,
  ShipType,
} from '@fv/client-types'
import { fvDate } from '@fv/dates'
import type {
  LocationAccessorialDTO,
  StopDateType,
  StopType,
  Workflow,
} from '@fv/models'
import { ShipTypeMapper } from '@fv/models'
import { quoteUtils } from '@fv/models/core'

import type { TypeNamesProvider } from './useTypeNames.js'

export function buildTrackStatusOptions(locations: ShipmentLocation[]) {
  const options = [{ text: 'Ready', value: '-1' }]

  locations.forEach(x => {
    const option = { text: `Stop ${x.sequence}`, value: x.sequence.toString() }
    if (x.sequence === 0) option.text = 'Picked up'
    if (x.sequence === locations.length - 1) option.text = 'Delivered'
    options.push(option)
  })

  return options
}

export function composeStopDetails({
  stopDate,
  stopDateType,
  stopType,
}: {
  stopDate?: string | Date | null
  stopDateType: StopDateType
  stopType: StopType
}) {
  const date = stopDate ? fvDate(stopDate).format('MMM D') : null
  let type = stopType === 'pickup' ? 'Pick up' : 'Deliver'

  if (stopType === 'both') type = 'Pick up and deliver'
  return `${type}${date ? ` ${stopDateType} ${date}` : ''}`
}

export function getTrackingNumLabel(
  workflow: Workflow,
  selectedQuote?: { providerCode?: string; assetCarrierCode?: string },
) {
  // For shipments booked with a broker, always show "Tracking#"
  if (quoteUtils.isBrokerQuote(selectedQuote)) {
    return 'Tracking#'
  }

  return workflow === 'ltl' ? 'PRO#' : 'Tracking#'
}

export function getCurrentSequence(tracking?: {
  currentLocation?: { sequence?: number }
  currentStatus?: string
}) {
  const { currentLocation } = tracking ?? {}

  if (typeof currentLocation?.sequence === 'number') {
    return currentLocation.sequence
  }

  return -1
}

export function tenderInProgressOrComplete({
  pickup,
}: {
  pickup?: PickupDetail
}) {
  return (
    pickup?.status === 'ok' ||
    pickup?.status === 'pending' ||
    pickup?.status === 'requesting'
  )
}

export function totalShipmentWeight({
  equipment,
  items,
}: Pick<FullShipment, 'equipment' | 'items'>) {
  const itemWeight = sumBy(items, 'weight')
  const totalWeight = itemWeight > 0 ? itemWeight : (equipment.weight ?? 0)
  return round(totalWeight, 2)
}

export function getItemLabels(
  documents: LoadDocument[] | undefined,
  itemId: string,
) {
  return (documents ?? []).filter(
    d => d.itemId === itemId && d.type === 'label',
  )
}

export function displayLocShipType({
  shipType,
  accessorials,
}: {
  shipType?: ShipType | ''
  accessorials?: LocationAccessorialDTO[]
}) {
  return ShipTypeMapper.displayLocShipType({ shipType, accessorials }) ?? null
}

export const locationAccessorialNormalizer = (provider: TypeNamesProvider) => {
  return (
    accessorial: LocationAccessorialDTO,
  ): Pick<LocationAccessorialDTO, 'key'> & {
    name: string
    appointmentTime?: string
  } => {
    return {
      ...(accessorial.key === 'schedule' && {
        appointmentTime: buildAppointmentTime(accessorial),
      }),
      key: accessorial.key,
      name: provider.accessorialName(accessorial.key),
    }
  }
}

export function getLocAccessorials(
  { accessorials }: Pick<ShipmentLocation, 'accessorials'>,
  provider: TypeNamesProvider,
) {
  const normalize = locationAccessorialNormalizer(provider)
  return accessorials
    ?.filter(a => a.key !== 'tradeshow')
    ?.map(a => normalize(a))
}

export function buildAppointmentTime(
  { opensAt, closesAt }: { opensAt?: string; closesAt?: string },
  opts?: { outputFormat: string },
) {
  const outFmt = opts?.outputFormat ?? 'h:mm a'
  const closeTime = fvDate(closesAt, 'HH:mm').format(outFmt)
  const openTime = fvDate(opensAt, 'HH:mm').format(outFmt)
  if (opensAt && closesAt) return `${openTime} to ${closeTime}`
  if (opensAt && !closesAt) return `After ${openTime}`
  if (!opensAt && closesAt) return `Before ${closeTime}`
  return undefined
}

// TODO: Utilize the isHazardous func from @fv/models
// Holding off right now because types are not aligned between FullShipment and ShipperShipment (Package types, objectIds, etc.)
export function isHazardous(shipment: FullShipment) {
  if (shipment.items?.length) {
    return shipment.items.some(item =>
      item.hazardous?.length
        ? true
        : item.contains?.some(c => c.hazardous?.length),
    )
  }

  return shipment.equipment.isHazardous ?? false
}

export function isBooked({ status }: Pick<FullShipment, 'status'>) {
  return status !== 'pending' && status !== 'canceled'
}
